import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ListingCard from '../components/ListingCard';
import { HomeSEO } from '../components/SEO';
import { Search, ArrowRight, ShieldCheck, Car, Wrench, Tag, ChevronRight } from 'lucide-react';

// ─── Filter Options (must match Browse.js) ───
const MAKES = ['Toyota', 'Honda', 'Nissan', 'Hyundai', 'Kia', 'Mazda', 'Mercedes-Benz', 'BMW', 'Audi', 'Volkswagen', 'Ford', 'Chevrolet', 'Mitsubishi'];
const LOCATIONS = ['Accra', 'Kumasi', 'Tema', 'Takoradi', 'Cape Coast', 'Tamale', 'Sunyani', 'Koforidua'];
const MAX_PRICE_BUCKETS = [
  { label: 'Under GHS 30k', value: '30000' },
  { label: 'Under GHS 50k', value: '50000' },
  { label: 'Under GHS 80k', value: '80000' },
  { label: 'Under GHS 150k', value: '150000' },
  { label: 'Under GHS 300k', value: '300000' },
];

const CATEGORIES = [
  { label: 'Sedans', icon: Car, query: { listing_type: 'car', search: 'sedan' } },
  { label: 'SUVs', icon: Car, query: { listing_type: 'car', search: 'suv' } },
  { label: 'Pickups', icon: Car, query: { listing_type: 'car', search: 'pickup' } },
  { label: 'Under GHS 50k', icon: Tag, query: { max_price: '50000' } },
  { label: 'Spare Parts', icon: Wrench, query: { listing_type: 'part' } },
];

// Curated Ghanaian street/road car photo (warm, retail)
const HERO_IMAGE = 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=2400&auto=format&fit=crop';

export default function Landing() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ make: '', location: '', max_price: '' });
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ active_listings: 0, sold_listings: 0, verified_dealers: 0, total_secured_ghs: 0 });
  const [loadingListings, setLoadingListings] = useState(true);

  useEffect(() => {
    API.get('/listings', { params: { limit: 8, sort: 'trust' } })
      .then(res => setFeatured(Array.isArray(res.data?.listings) ? res.data.listings : []))
      .catch(() => setFeatured([]))
      .finally(() => setLoadingListings(false));
    API.get('/public/stats').then(res => setStats(res.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const qs = new URLSearchParams();
    if (filters.make) qs.set('make', filters.make);
    if (filters.location) qs.set('location', filters.location);
    if (filters.max_price) qs.set('max_price', filters.max_price);
    navigate(`/browse${qs.toString() ? `?${qs}` : ''}`);
  };

  const browseCategory = (q) => {
    const qs = new URLSearchParams(q).toString();
    navigate(`/browse?${qs}`);
  };

  const fmtGHS = (n) => {
    if (!n) return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1000) return `${Math.round(n / 1000)}k`;
    return String(n);
  };

  return (
    <main data-testid="landing-page">
      <HomeSEO />

      {/* ─── HERO + INTEGRATED SEARCH ─── */}
      <section className="relative isolate">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/70 to-background" />
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 pt-20 md:pt-28 pb-12 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <p className="text-xs md:text-sm tracking-[0.18em] uppercase text-gold mb-4 font-medium">AutoTrust Ghana</p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight" data-testid="hero-headline">
              Ghana&rsquo;s automotive<br className="hidden md:block" /> marketplace.
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
              Buy and sell cars and parts with verified sellers, secure escrow payments, and AI-backed listings.
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.form
            onSubmit={submitSearch}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
            className="mt-8 md:mt-10 bg-card/95 backdrop-blur-xl border border-border/60 rounded-2xl p-3 md:p-3 shadow-2xl max-w-4xl"
            data-testid="hero-search-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
              <Select value={filters.make} onValueChange={(v) => setFilters(f => ({ ...f, make: v === '_any' ? '' : v }))}>
                <SelectTrigger className="h-12 border-0 bg-transparent hover:bg-muted/50 focus:ring-0 focus:ring-offset-0" data-testid="hero-make">
                  <SelectValue placeholder="Any make" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any">Any make</SelectItem>
                  {MAKES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.location} onValueChange={(v) => setFilters(f => ({ ...f, location: v === '_any' ? '' : v }))}>
                <SelectTrigger className="h-12 border-0 bg-transparent hover:bg-muted/50 focus:ring-0 focus:ring-offset-0" data-testid="hero-location">
                  <SelectValue placeholder="Any location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any">Any location</SelectItem>
                  {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.max_price} onValueChange={(v) => setFilters(f => ({ ...f, max_price: v === '_any' ? '' : v }))}>
                <SelectTrigger className="h-12 border-0 bg-transparent hover:bg-muted/50 focus:ring-0 focus:ring-offset-0" data-testid="hero-price">
                  <SelectValue placeholder="Any price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_any">Any price</SelectItem>
                  {MAX_PRICE_BUCKETS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit" className="h-12 px-6 bg-gold text-black hover:brightness-110 font-semibold gap-2" data-testid="hero-search-submit">
                <Search className="w-4 h-4" /> Search
              </Button>
            </div>
          </motion.form>

          {/* Trust Strip - live counters */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 md:mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground"
            data-testid="hero-trust-strip"
          >
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-trust-green" />
              <span className="font-mono-display font-semibold text-foreground">{stats.active_listings}</span> vehicles available
            </span>
            <span className="hidden md:inline text-border">·</span>
            <span>
              <span className="font-mono-display font-semibold text-foreground">{stats.verified_dealers}</span> verified dealers
            </span>
            <span className="hidden md:inline text-border">·</span>
            <span>
              <span className="font-mono-display font-semibold text-foreground">GHS {fmtGHS(stats.total_secured_ghs)}</span> secured through escrow
            </span>
          </motion.div>
        </div>
      </section>

      {/* ─── FEATURED INVENTORY ─── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Inventory</p>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Featured vehicles</h2>
          </div>
          <Link to="/browse" className="text-sm font-medium text-gold hover:underline flex items-center gap-1" data-testid="see-all-inventory">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loadingListings ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-skeleton">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-muted/40 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            No listings yet. <Link to="/sell" className="text-gold underline">Be the first to list a car</Link>.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-grid">
            {featured.map((l, i) => (
              <motion.div
                key={l.listing_id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.25), ease: 'easeOut' }}
              >
                <ListingCard listing={l} />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* ─── SHOP BY CATEGORY ─── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16 border-t border-border/40">
        <div className="mb-6 md:mb-8">
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Browse</p>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Shop by category</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="category-grid">
          {CATEGORIES.map(({ label, icon: Icon, query }, i) => (
            <button
              key={label}
              onClick={() => browseCategory(query)}
              className="group flex flex-col items-start gap-3 p-5 rounded-xl border border-border/50 bg-card hover:border-gold/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-200 text-left"
              data-testid={`category-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center text-gold group-hover:bg-gold/20 transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ─── SELLERS / TRUST FOOTER STRIP ─── */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16 border-t border-border/40">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Sellers</p>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">List your car in under 5 minutes.</h2>
            <p className="text-muted-foreground mb-6 max-w-md">
              Verified profile, AI-enhanced photos, secure escrow payouts. We handle the trust — you handle the keys.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/sell">
                <Button className="bg-gold text-black hover:brightness-110 font-semibold gap-2" data-testid="cta-sell">
                  Start a listing <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/trust">
                <Button variant="outline" className="gap-2" data-testid="cta-trust">
                  How AutoTrust works <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-5 rounded-xl border border-border/50 bg-card">
              <p className="font-mono-display text-2xl md:text-3xl font-bold text-gold">{stats.active_listings}</p>
              <p className="text-xs text-muted-foreground mt-1">Active listings</p>
            </div>
            <div className="p-5 rounded-xl border border-border/50 bg-card">
              <p className="font-mono-display text-2xl md:text-3xl font-bold text-trust-green">{stats.sold_listings}</p>
              <p className="text-xs text-muted-foreground mt-1">Cars sold</p>
            </div>
            <div className="p-5 rounded-xl border border-border/50 bg-card">
              <p className="font-mono-display text-2xl md:text-3xl font-bold">GHS {fmtGHS(stats.total_secured_ghs)}</p>
              <p className="text-xs text-muted-foreground mt-1">Secured</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
